import { useState } from 'react'

import { OneDataCollection } from '@factorialco/factorial-one-react/dist/experimental'
import { Upload } from '@factorialco/factorial-one-react/icons/app'
import { BenefitsUsagePageDataCollectionDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { ExportSidepanel } from 'modules/universal_exports/components/ExportSidepanel'

import { extractEmployeeData } from './extractEmployeeData'

interface UseDataCollectionProps {
  employeeIds: number[]
  startDate: Date
  endDate: Date
}

/**
 * HACK: This wrapper is a band-aid while we investigate a deficiency in the
 * useGraphqlDataSource hook, which isn't refetching when the dependencies
 * provided the hook change. When that is fixed we should remove this
 * wrapper component, and we can use the date navigation filter available
 * to OneDataCollection.
 */
export const UsageDataCollection = ({
  employeeIds,
  startDate,
  endDate,
}: UseDataCollectionProps) => {
  const [showExportModal, setShowExportModal] = useState(false)

  const { access } = useCurrentSession()
  const locale = access.locale || 'es'

  const source = useGraphqlDataSource(
    {
      document: BenefitsUsagePageDataCollectionDocument,
      mapVariables: ({ search }) => ({
        // Adding -1 because with empty array it returns all the employees
        employeeIds: employeeIds?.length > 0 ? employeeIds : [-1],
        search,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      }),
      mapData: extractEmployeeData,
      mapPagination: (data) => ({
        total: data.employees.employeeNamesConnection.totalCount ?? 0,
      }),
      search: {
        enabled: true,
      },
      primaryActions: () => ({
        label: i18n.t('common.export'),
        onClick: () => setShowExportModal(true),
        icon: Upload,
      }),
    },
    [startDate, endDate, employeeIds]
  )

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('benefits.tabs.usage.page.table.columns.employee'),
                  render: (item) => {
                    return {
                      type: 'person',
                      value: {
                        firstName: item.firstName,
                        lastName: item.lastName,
                        src: buildUrl(item.avatar?.url ?? ''),
                      },
                    }
                  },
                },
                {
                  label: i18n.t('benefits.tabs.usage.page.table.columns.total_amount'),
                  render: (item) =>
                    formatCurrency(
                      Object.values(item.amounts).reduce((acc, curr) => acc + curr, 0),
                      locale
                    ),
                },
                {
                  label: i18n.t('benefits.tabs.usage.page.table.columns.meals'),
                  render: (item) => formatCurrency(item.amounts.meals, locale),
                },
                {
                  label: i18n.t('benefits.tabs.usage.page.table.columns.transport'),
                  render: (item) => formatCurrency(item.amounts.transportation, locale),
                },
                {
                  label: i18n.t('benefits.tabs.usage.page.table.columns.childcare'),
                  render: (item) => formatCurrency(item.amounts.childcare, locale),
                },
              ],
              frozenColumns: 1,
            },
          },
        ]}
      />
      {showExportModal && (
        <ExportSidepanel
          resource="benefits.benefit_compensation"
          onClose={() => setShowExportModal(false)}
        />
      )}
    </>
  )
}

const formatCurrency = (amount: number, locale: string) => {
  if (amount === 0) {
    return '-'
  }

  return formattedAmount({
    amountCents: amount,
    currency: 'EUR',
    locale,
  })
}
